import { css, Theme } from '@emotion/react';
import styled from '@emotion/styled';
import { Checkbox } from '@mui/material';

/**
 * The dense action list: one bordered container whose rows are divided by hairlines rather than a stack of
 * separately-bordered cards. Shared by `ItemActionList` and the feature rows that follow its idiom
 * (`ScheduledTaskCard`, `AiHistoryCard`) so the three can't drift apart.
 *
 * This is deliberately a style module rather than a row component: the three consumers agree on the
 * geometry down to the pixel but disagree on the markup — a checkbox here, a third line there, a toggle in
 * the action rail — and a component covering all of that would need a slot per element, which is no simpler
 * than the JSX it replaced. Note that this is a *third* row family in `core/`, distinct from the older
 * `CommonRowStyles` (`ActionRowBase`, per-row `border-bottom`) and from the table-oriented
 * `CommonListingStyles`; reach for those when extending the surfaces already built on them.
 */

/** The bordered card holding the rows. Its `overflow` is what rounds the first and last row's corners. */
export const ActionListCard = styled.div`
    display: flex;
    flex-direction: column;
    border: 1px solid ${(props) => props.theme.color.border};
    border-radius: 0.75rem;
    overflow: hidden;
    background: ${(props) => props.theme.color.background};
`;

/**
 * The rule between two rows: the container's own border colour at reduced alpha, so a divider inside the
 * card reads lighter than the card's edge. One value because a hand-written alpha suffix is easy to mistype
 * and impossible to spot once it has drifted between surfaces.
 */
const rowDividerBorder = (theme: Theme) => `1px solid ${theme.color.border + '60'}`;

/** Hairline above every row but the first, keyed off adjacency — for rows that all render as one element. */
export const MixinAdjacentRowDivider = (theme: Theme) => css`
    & + & {
        border-top: ${rowDividerBorder(theme)};
    }
`;

/**
 * The same hairline keyed off position rather than adjacency. Required whenever a row's styling varies by
 * state (selected, paused): emotion emits a different class per variant, so `& + &` matches only pairs that
 * happen to share a state — silently dropping the rule above and below every varying row. `:not(:first-of-type)`
 * is evaluated per row instead, so it holds whatever the mix. `:first-of-type` rather than `:first-child`
 * because every current row list is homogeneous (each container's children are all the same row tag), and
 * `:first-child` trips React's "unsafe with SSR" warning that `:first-of-type` doesn't.
 */
export const MixinPositionalRowDivider = (theme: Theme) => css`
    &:not(:first-of-type) {
        border-top: ${rowDividerBorder(theme)};
    }
`;

/** Single-line truncation. Not `MixinTruncate`, which line-clamps and breaks mid-word. */
export const MixinRowTruncate = css`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

/**
 * One row of the list. Rows are separated by a rule rather than spaced apart: a label and its tag sit on the
 * same line, so the gap between two rows has to be narrower than the gap within one for the pairing to hold.
 */
export const ActionListRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.5rem 0.875rem;
`;

/**
 * The multi-line variant, for a row whose text column runs to two or three lines. Taller than the single-line
 * row, and the contents align to the top rather than the centre so a trailing action rail stays level with
 * the title — centring would leave it floating against the metadata.
 *
 * The divider is left to the caller: these rows usually carry per-state styling and so need
 * {@link MixinPositionalRowDivider} on whichever element owns that state.
 */
export const ActionListRowBody = styled(ActionListRow)`
    align-items: flex-start;
    padding: 0.625rem 0.875rem;
    transition: background 0.15s ease;

    &:hover {
        background: ${(props) => props.theme.color.cardHover};
    }
`;

export const RowCheckbox = styled(Checkbox)`
    flex: 0 0 auto;
    padding: 0;
`;

/**
 * Everything in a multi-line row that opens it — icon and text column. Flex rather than a grid column so
 * that a row rendered without its action rail doesn't keep a phantom track and its gap.
 */
export const RowMain = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 0.625rem;
    flex: 1;
    min-width: 0;
    cursor: pointer;
`;

/**
 * The leading icon slot of a multi-line row. Fixed-size so the icon never shrinks; the padding drops it onto
 * the title's optical centre, its box being shorter than the title's line box. Colour is left to the caller —
 * a row that codes its kind by colour sets its own.
 */
export const RowIcon = styled.span`
    display: inline-flex;
    flex: 0 0 auto;
    padding-top: 0.0625rem;
`;

export const RowText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    min-width: 0;
    flex: 1;
`;

export const RowTitleLine = styled.div`
    display: flex;
    align-items: center;
    gap: 0.4rem;
    min-width: 0;
`;

export const RowTitle = styled.span`
    min-width: 0;
    font-size: 0.8125rem;
    font-weight: 600;
    color: ${(props) => props.theme.color.text};
    ${MixinRowTruncate}
`;

/**
 * The dense metadata line under the title — the wrapping row of tags and labels the Tiny family is built
 * for, rather than stacked paragraphs.
 */
export const RowMetaLine = styled.div`
    display: flex;
    align-items: center;
    gap: 0.25rem 0.75rem;
    flex-wrap: wrap;
    min-width: 0;
`;

/** The trailing rail of icon-only actions. */
export const RowActions = styled.div`
    display: flex;
    align-items: center;
    gap: 0.125rem;
    flex-shrink: 0;
`;
